import { useState, useEffect, useRef } from "react";
import { Search, Globe, X, Loader2, Check, Anchor } from "lucide-react";
import { api } from "@/lib/api";

/**
 * VesselLinkPicker — search & link a Cruiser Contact to an opted-in public
 * vessel. When linked, the contact card pulls in that vessel's bio, photo,
 * and hailing port automatically.
 *
 * Privacy posture: search hits authenticated `/api/public-vessels/search`,
 * which already filters to opted-in vessels excluding the requesting user's
 * own. We can't accidentally link to private data.
 *
 * Props:
 *   value: { linked_vessel_id, linked_profile } — current link state
 *   onChange: (next) => void  — receives { linked_vessel_id, linked_profile_preview }
 *   initialQuery: string — seed search from the contact's boat_name
 */
export default function VesselLinkPicker({ value, onChange, initialQuery = "" }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(initialQuery);
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const popoverRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const { data } = await api.get(`/public-vessels/search?q=${encodeURIComponent(query.trim())}`);
                setResults(data || []);
            } catch (_e) {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 250);
        return () => clearTimeout(debounceRef.current);
    }, [query, open]);

    const link = (vessel) => {
        onChange({
            linked_vessel_id: vessel.id,
            linked_profile_preview: vessel,  // for instant UI feedback
        });
        setOpen(false);
    };

    const unlink = () => {
        onChange({ linked_vessel_id: null, linked_profile_preview: null });
    };

    const linkedProfile = value?.linked_profile || value?.linked_profile_preview;

    if (value?.linked_vessel_id && linkedProfile) {
        // Currently linked — show a card-style summary with unlink button
        return (
            <div
                data-testid="vessel-link-linked"
                style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: "rgba(200, 162, 92, 0.08)",
                    border: "1px solid var(--bm-gold-dim)",
                }}
            >
                <Globe size={14} style={{ color: "var(--bm-gold)", flexShrink: 0 }} />
                {linkedProfile.hero_photo_url && (
                    <img
                        src={linkedProfile.hero_photo_url}
                        alt=""
                        style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover", border: "1px solid var(--bm-border-soft)" }}
                    />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--bm-text)" }}>
                        {linkedProfile.name}
                    </div>
                    {linkedProfile.public_bio && (
                        <div style={{ fontSize: 11, color: "var(--bm-text-dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {linkedProfile.public_bio}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    data-testid="vessel-link-unlink-btn"
                    onClick={unlink}
                    className="bm-btn bm-btn-ghost"
                    style={{ padding: "4px 8px", fontSize: 11 }}
                    aria-label="Unlink Bosun's Mate boat"
                >
                    <X size={11} /> Unlink
                </button>
            </div>
        );
    }

    // Not yet linked — show the search trigger
    return (
        <div style={{ position: "relative" }}>
            <button
                type="button"
                data-testid="vessel-link-trigger"
                onClick={() => setOpen((v) => !v)}
                className="bm-btn bm-btn-ghost"
                style={{ fontSize: 12, padding: "8px 12px" }}
            >
                <Globe size={12} /> Link to a Bosun&apos;s Mate boat
            </button>

            {open && (
                <div
                    ref={popoverRef}
                    data-testid="vessel-link-popover"
                    style={{
                        position: "absolute", top: "calc(100% + 6px)", left: 0,
                        zIndex: 200, width: "min(380px, 100%)",
                        background: "var(--bm-bg-2, #111e30)",
                        border: "1px solid var(--bm-gold-dim)",
                        borderRadius: 10,
                        boxShadow: "0 12px 36px -10px rgba(0,0,0,0.55)",
                        padding: 12,
                    }}
                >
                    <div style={{ position: "relative", marginBottom: 10 }}>
                        <Search size={13} style={{
                            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                            color: "var(--bm-text-dim)",
                        }} />
                        <input
                            data-testid="vessel-link-search-input"
                            className="bm-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search public boats by name…"
                            style={{ paddingLeft: 32, fontSize: 13 }}
                            autoFocus
                        />
                    </div>

                    <div style={{ maxHeight: 250, overflowY: "auto" }}>
                        {searching && (
                            <div style={{ padding: 14, textAlign: "center", fontSize: 12, color: "var(--bm-text-muted)" }}>
                                <Loader2 size={13} className="bm-spin" /> Searching…
                            </div>
                        )}
                        {!searching && results.length === 0 && query.trim() && (
                            <p style={{ margin: "8px 0", fontSize: 12, color: "var(--bm-text-muted)", lineHeight: 1.5 }}>
                                No public boats match &quot;{query}&quot;.
                                <br />
                                <span style={{ color: "var(--bm-text-dim)" }}>
                                    Only captains who&apos;ve opted in to a public profile appear here.
                                </span>
                            </p>
                        )}
                        {!searching && results.length === 0 && !query.trim() && (
                            <p style={{ margin: "8px 0", fontSize: 12, color: "var(--bm-text-dim)" }}>
                                Start typing a boat name…
                            </p>
                        )}
                        {!searching && results.map((v) => (
                            <button
                                key={v.id}
                                type="button"
                                data-testid={`vessel-link-result-${v.id}`}
                                onClick={() => link(v)}
                                className="bm-btn bm-btn-ghost"
                                style={{
                                    display: "flex", alignItems: "flex-start", gap: 10,
                                    width: "100%", textAlign: "left", padding: "8px 10px",
                                    marginBottom: 4, fontWeight: 400, whiteSpace: "normal",
                                }}
                            >
                                {v.hero_photo_url ? (
                                    <img src={v.hero_photo_url} alt="" style={{
                                        width: 36, height: 36, borderRadius: 6,
                                        objectFit: "cover", border: "1px solid var(--bm-border-soft)",
                                        flexShrink: 0,
                                    }} />
                                ) : (
                                    <div style={{
                                        width: 36, height: 36, borderRadius: 6,
                                        background: "rgba(200, 162, 92, 0.12)",
                                        border: "1px solid var(--bm-gold-dim)",
                                        display: "grid", placeItems: "center", flexShrink: 0,
                                    }}>
                                        <Anchor size={14} style={{ color: "var(--bm-gold)" }} />
                                    </div>
                                )}
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: "block", fontSize: 13, color: "var(--bm-text)" }}>
                                        {v.name}
                                    </span>
                                    <span style={{ display: "block", fontSize: 11, color: "var(--bm-text-dim)", marginTop: 2 }}>
                                        {[v.vessel_type, v.public_hailing_port].filter(Boolean).join(" · ")}
                                    </span>
                                    {v.public_bio && (
                                        <span style={{ display: "block", fontSize: 11, color: "var(--bm-text-muted)", marginTop: 3, lineHeight: 1.4 }}>
                                            {v.public_bio}
                                        </span>
                                    )}
                                </span>
                                <Check size={12} style={{ color: "var(--bm-gold)", flexShrink: 0, marginTop: 12 }} />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
